/**
 * Directed causal / precedence graph utilities (#222, `06-timeline.md` §8).
 *
 * Two Event relations compose into a single "happens-before" directed graph:
 *
 *  - **`caused_by`** — a fact `(A caused_by B)` asserts that B *caused* A, so B
 *    happens before A: the edge points `B → A`
 *    ({@link CausalDirection} `'object_before_subject'`).
 *  - **`precedes`** — a fact `(A precedes B)` asserts that A happens before B:
 *    the edge points `A → B` (`'subject_before_object'`).
 *
 * Normalising both into one edge set means the two relations interlock: a
 * `caused_by` and a `precedes` that disagree about the order of the same pair
 * form a 2-cycle, exactly as two mutually-`caused_by` events do. A directed
 * **cycle** in the composed graph is a causal paradox (the auditor's
 * `causal.cycle` rule); a **topological order** is the causal timeline (the
 * Overview "Causal chain" view). Both consumers build edges from their own fact
 * representation via {@link causalEdge} and call {@link analyzeDirectedGraph};
 * the only domain knowledge they inject is opaque predicate identity (ADR-0004
 * §1) and per-predicate direction — the engine stays English-free and
 * pack-agnostic.
 *
 * Every routine is pure, synchronous, and deterministic: node iteration and
 * adjacency are processed in sorted id order so the topological order and the
 * canonicalised cycle list are stable across runs (load-bearing for the
 * auditor's content-addressed advisory ids and for snapshot tests).
 */

import { OFFICIAL_CORE_PREDICATE_IDS } from './packs/official-core-ids.js';

/** Which endpoint of a causal fact happens first. */
export type CausalDirection = 'object_before_subject' | 'subject_before_object';

/**
 * A causal/precedence predicate and which way its "happens-before" edge points.
 * Supplied by the caller (the client passes the official-core `caused_by` /
 * `precedes` ids — see {@link OFFICIAL_CORE_CAUSAL_PREDICATES}); identity is an
 * opaque id with no semantic flag on the declaration, so the engine never
 * hard-codes it.
 */
export interface CausalPredicateSpec {
  readonly predicate: string;
  readonly direction: CausalDirection;
}

/**
 * The official-core binding of {@link CausalPredicateSpec} — `caused_by`
 * (object happens before subject) and `precedes` (subject before object). The
 * single source of truth shared by the auditor's `causal.cycle` rule wiring and
 * the client's "Causal chain" view, so the two never drift on which predicates
 * (or directions) form the happens-before graph. A homebrew pack that defines
 * its own causal vocabulary would extend this list at the call site.
 */
export const OFFICIAL_CORE_CAUSAL_PREDICATES: readonly CausalPredicateSpec[] = [
  { predicate: OFFICIAL_CORE_PREDICATE_IDS.caused_by, direction: 'object_before_subject' },
  { predicate: OFFICIAL_CORE_PREDICATE_IDS.precedes, direction: 'subject_before_object' },
];

/** A directed edge `from → to` ("`from` happens before `to`"). */
export interface DirectedEdge {
  readonly from: string;
  readonly to: string;
  /**
   * Optional provenance carried verbatim alongside the edge (e.g. a
   * `factRef(subject, factKey)`), so a consumer that detects a cycle can point
   * back at the exact facts forming it. Not used by the graph algorithm.
   */
  readonly via?: string;
}

/**
 * Normalise a causal fact `(subject {predicate} object)` into a happens-before
 * edge, flipping the direction for `caused_by`-style relations so the edge
 * always points from the earlier event to the later one.
 */
export function causalEdge(
  subject: string,
  object: string,
  direction: CausalDirection,
  via?: string,
): DirectedEdge {
  const [from, to] =
    direction === 'object_before_subject' ? [object, subject] : [subject, object];
  return via === undefined ? { from, to } : { from, to, via };
}

/** One detected directed cycle. */
export interface CausalCycle {
  /**
   * The node ids forming the loop, in traversal order. The first node is NOT
   * repeated at the end (`[a, b, c]` means `a → b → c → a`). Canonicalised so
   * the lexicographically-smallest id is first — the same loop discovered from
   * any start node yields the same array.
   */
  readonly nodes: readonly string[];
  /**
   * The `via` provenance of each edge in the loop, parallel to `nodes`:
   * `edges[i]` is the edge `nodes[i] → nodes[(i + 1) % n]`. Entries are
   * `undefined` when the contributing edge carried no `via`.
   */
  readonly edges: readonly (string | undefined)[];
}

/** The result of analysing a directed graph. */
export interface DirectedGraphAnalysis {
  /**
   * A topological order (causes before effects). When the graph is a DAG this
   * is a full ordering of every node; when cycles exist the acyclic portion is
   * ordered first (Kahn's algorithm) and the nodes still trapped in cycles are
   * appended in sorted id order, so the result is always a deterministic total
   * ordering of every node.
   */
  readonly order: readonly string[];
  /** Every distinct directed cycle (canonicalised + de-duplicated). Empty ⇒ a DAG. */
  readonly cycles: readonly CausalCycle[];
  /** Convenience flag — `true` iff `cycles` is non-empty. */
  readonly acyclic: boolean;
}

/** Rotate a cycle so its smallest node id is first, keeping `via` aligned. */
function canonicalizeCycle(nodes: string[], edges: (string | undefined)[]): CausalCycle {
  let min = 0;
  for (let i = 1; i < nodes.length; i++) {
    if (nodes[i]! < nodes[min]!) min = i;
  }
  const n = nodes.length;
  const rotatedNodes: string[] = [];
  const rotatedEdges: (string | undefined)[] = [];
  for (let i = 0; i < n; i++) {
    rotatedNodes.push(nodes[(min + i) % n]!);
    rotatedEdges.push(edges[(min + i) % n]!);
  }
  return { nodes: rotatedNodes, edges: rotatedEdges };
}

/**
 * Analyse a directed graph: find its directed cycles and a deterministic
 * topological order.
 *
 * Cycle detection is a colour-marked DFS (white / grey / black); each back edge
 * onto a grey ancestor reconstructs the loop along the current DFS path. Loops
 * are canonicalised (rotated to a smallest-id-first form) and de-duplicated, so
 * the same cycle reported from different start nodes collapses to one entry.
 * (The set of *back-edge* cycles is what the auditor flags — it is not the full
 * enumeration of every simple cycle, but it is non-empty exactly when the graph
 * is cyclic and pinpoints concrete offending facts to break.)
 *
 * The topological order uses Kahn's algorithm over the full node set with a
 * sorted ready-queue for determinism; nodes left with non-zero in-degree (those
 * inside cycles) are appended in sorted id order.
 */
export function analyzeDirectedGraph(
  nodes: Iterable<string>,
  edges: Iterable<DirectedEdge>,
): DirectedGraphAnalysis {
  const nodeSet = new Set<string>(nodes);
  // Adjacency + in-degree. Edges to/from unknown nodes pull those nodes in so
  // a caller may pass edges alone and rely on endpoint discovery.
  const adj = new Map<string, DirectedEdge[]>();
  for (const e of edges) {
    nodeSet.add(e.from);
    nodeSet.add(e.to);
    const list = adj.get(e.from);
    if (list) list.push(e);
    else adj.set(e.from, [e]);
  }
  const sortedNodes = [...nodeSet].sort();
  // Deterministic adjacency: sort each out-edge list by (to, via).
  for (const list of adj.values()) {
    list.sort((a, b) => (a.to === b.to ? (a.via ?? '').localeCompare(b.via ?? '') : a.to.localeCompare(b.to)));
  }

  // ---- Cycle detection (colour-marked DFS) ----
  const WHITE = 0,
    GREY = 1,
    BLACK = 2;
  const colour = new Map<string, number>();
  for (const n of sortedNodes) colour.set(n, WHITE);
  const cycles: CausalCycle[] = [];
  const seen = new Set<string>();

  // Iterative DFS to avoid blowing the call stack on long chains. The path
  // arrays mirror a recursion stack: `pathNodes[i]` reached via `pathVia[i]`.
  for (const root of sortedNodes) {
    if (colour.get(root) !== WHITE) continue;
    const pathNodes: string[] = [];
    const pathVia: (string | undefined)[] = [];
    // Frame: the node plus an index into its (sorted) out-edge list.
    const frames: { node: string; edgeIndex: number; via: string | undefined }[] = [
      { node: root, edgeIndex: 0, via: undefined },
    ];
    colour.set(root, GREY);
    pathNodes.push(root);
    pathVia.push(undefined);

    while (frames.length > 0) {
      const frame = frames[frames.length - 1]!;
      const out = adj.get(frame.node) ?? [];
      if (frame.edgeIndex < out.length) {
        const edge = out[frame.edgeIndex]!;
        frame.edgeIndex++;
        const next = edge.to;
        const nextColour = colour.get(next);
        if (nextColour === GREY) {
          // Back edge → cycle from `next` down the current path to `frame.node`.
          const start = pathNodes.lastIndexOf(next);
          if (start !== -1) {
            const loopNodes = pathNodes.slice(start);
            // Edge i of the loop is the via that *entered* loopNodes[i+1];
            // the closing edge (last → first) is this back edge's via.
            const loopEdges: (string | undefined)[] = [];
            for (let i = start + 1; i < pathNodes.length; i++) loopEdges.push(pathVia[i]);
            loopEdges.push(edge.via);
            const canonical = canonicalizeCycle(loopNodes, loopEdges);
            const key = canonical.nodes.join(' ');
            if (!seen.has(key)) {
              seen.add(key);
              cycles.push(canonical);
            }
          }
        } else if (nextColour === WHITE) {
          colour.set(next, GREY);
          pathNodes.push(next);
          pathVia.push(edge.via);
          frames.push({ node: next, edgeIndex: 0, via: edge.via });
        }
      } else {
        colour.set(frame.node, BLACK);
        frames.pop();
        pathNodes.pop();
        pathVia.pop();
      }
    }
  }
  cycles.sort((a, b) => a.nodes.join(' ').localeCompare(b.nodes.join(' ')));

  // ---- Topological order (Kahn's, sorted ready-queue) ----
  const indegree = new Map<string, number>();
  for (const n of sortedNodes) indegree.set(n, 0);
  for (const list of adj.values()) {
    for (const e of list) indegree.set(e.to, (indegree.get(e.to) ?? 0) + 1);
  }
  const order: string[] = [];
  const placed = new Set<string>();
  // Re-derive the ready set each round from sorted nodes to keep it ordered.
  let progress = true;
  while (progress) {
    progress = false;
    for (const n of sortedNodes) {
      if (placed.has(n)) continue;
      if ((indegree.get(n) ?? 0) !== 0) continue;
      placed.add(n);
      order.push(n);
      progress = true;
      for (const e of adj.get(n) ?? []) {
        if (placed.has(e.to)) continue;
        indegree.set(e.to, (indegree.get(e.to) ?? 0) - 1);
      }
    }
  }
  // Nodes still trapped in cycles: append in sorted id order.
  for (const n of sortedNodes) if (!placed.has(n)) order.push(n);

  return { order, cycles, acyclic: cycles.length === 0 };
}
